/** Pure logic for the Record a payment screen. */
export type Method = 'check' | 'money_order' | 'cash' | 'card' | 'bank'
export type ManualMethod = 'check' | 'money_order' | 'cash'

export const isManual = (m: Method): m is ManualMethod => m === 'check' || m === 'money_order' || m === 'cash'

export const METHOD_CARDS: { method: Method; label: string; sub: string }[] = [
  { method: 'check', label: 'Check', sub: 'Posted to us' },
  { method: 'money_order', label: 'Money order', sub: 'Posted to us' },
  { method: 'cash', label: 'Cash', sub: 'Post or in person' },
  { method: 'card', label: 'Card', sub: 'Charged now' },
  { method: 'bank', label: 'eCheck', sub: 'Debited from bank' },
]

const WORD: Record<Method, string> = {
  check: 'check', money_order: 'money order', cash: 'cash', card: 'card', bank: 'eCheck',
}
const PREFIX: Record<ManualMethod, string> = { check: 'CHK', money_order: 'MO', cash: 'CASH' }

export function dollars(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export const linesTotal = (lines: { price_cents: number }[]): number =>
  lines.reduce((s, l) => s + (Number.isFinite(l.price_cents) ? l.price_cents : 0), 0)

/** "$1,074.90" or "74.9" to cents; anything unreadable is 0. */
export function parseMoney(raw: string): number {
  const clean = raw.replace(/[$,\s]/g, '')
  if (!/^\d*\.?\d{0,2}$/.test(clean) || clean === '' || clean === '.') return 0
  return Math.round(parseFloat(clean) * 100)
}

export const centsToInput = (cents: number): string => (cents / 100).toFixed(2)

export function balance(method: Method, amountCents: number, lines: { price_cents: number }[]): { ok: boolean; text: string } {
  const total = linesTotal(lines)
  if (!isManual(method)) return { ok: total > 0, text: `Charge total: ${dollars(total)}.` }
  const word = WORD[method]
  if (amountCents <= 0) return { ok: false, text: `Enter the amount of the ${word}.` }
  if (total === 0) return { ok: false, text: 'Add what the payment is for.' }
  const diff = amountCents - total
  if (diff === 0) return { ok: true, text: `Lines add up to ${dollars(total)}, the same as the ${word}.` }
  return {
    ok: false,
    text: `The ${word} is ${dollars(Math.abs(diff))} ${diff > 0 ? 'more' : 'less'} than the lines (${dollars(total)}). Fix the amount or the lines before saving.`,
  }
}

export function saveLabel(method: Method, ref: string, total: number): string {
  switch (method) {
    case 'check': return ref.trim() ? `Record check #${ref.trim()}` : 'Record check'
    case 'money_order': return 'Record money order'
    case 'cash': return 'Record cash payment'
    case 'card': return `Charge card ${dollars(total)}`
    case 'bank': return `Debit account ${dollars(total)}`
  }
}

/** The order key each line will get; a repeat title gets -2, -3 like the server. */
export function orderKeyPreview(method: Method, acct: string, ref: string, slugs: string[]): string[] {
  if (!isManual(method)) return ['Authorize.Net transaction id, once approved']
  const seen: Record<string, number> = {}
  const base = ref.trim() || '[reference]'
  return slugs.map((s) => {
    seen[s] = (seen[s] ?? 0) + 1
    return `${PREFIX[method]}-${acct || '[account]'}-${base}${seen[s] > 1 ? `-${seen[s]}` : ''}-${s}`
  })
}

export interface FieldSpec {
  key: 'reference' | 'amount' | 'reference_date' | 'received_date' | 'bill_zip' | 'name_on_account' | 'account_type' | DetailKey
  label: string
  kind: 'text' | 'money' | 'date' | 'select'
  required: boolean
  placeholder?: string
  options?: { value: string; label: string }[]
}

/** Mirrors aamcirc/desk/common.py MONEY_ORDER_ISSUERS (codes). */
export const MONEY_ORDER_ISSUERS = [
  { value: 'usps', label: 'USPS' }, { value: 'western_union', label: 'Western Union' },
  { value: 'moneygram', label: 'MoneyGram' }, { value: 'bank', label: 'A bank' }, { value: 'other', label: 'Other' },
]
/** Mirrors aamcirc/desk/common.py CASH_WHERE (codes). */
export const CASH_WHERE = [
  { value: 'post', label: 'By post' }, { value: 'event', label: 'At a show or event' }, { value: 'office', label: 'At the office' },
]

/** Each hand-taken method's own detail, kept on the payment (optional). */
export type DetailKey = 'name_on_check' | 'money_order_issuer' | 'cash_where'
const DETAIL: Record<ManualMethod, FieldSpec> = {
  check: { key: 'name_on_check', label: 'Name on the check', kind: 'text', required: false, placeholder: 'If not the member' },
  money_order: { key: 'money_order_issuer', label: 'Issued by', kind: 'select', required: false, options: [{ value: '', label: 'Not noted' }, ...MONEY_ORDER_ISSUERS] },
  cash: { key: 'cash_where', label: 'How it came in', kind: 'select', required: false, options: [{ value: '', label: 'Not noted' }, ...CASH_WHERE] },
}
export const detailField = (method: ManualMethod): FieldSpec => DETAIL[method]

/** The detail field for the current method only; a value left from another method is not sent. */
export function methodDetailBody(method: Method, vals: Partial<Record<string, string>>): Partial<Record<DetailKey, string>> {
  if (!isManual(method)) return {}
  const key = DETAIL[method].key as DetailKey
  const v = (vals[key] ?? '').trim()
  return v ? { [key]: v } : {}
}

const AMOUNT = (label: string): FieldSpec => ({ key: 'amount', label, kind: 'money', required: true })
const RECEIVED: FieldSpec = { key: 'received_date', label: 'Received', kind: 'date', required: true }

export function methodFields(method: Method): FieldSpec[] {
  switch (method) {
    case 'check':
      return [
        { key: 'reference', label: 'Check number', kind: 'text', required: true },
        AMOUNT('Amount on the check'),
        { key: 'reference_date', label: 'Date on the check', kind: 'date', required: false },
        RECEIVED,
        DETAIL.check,
      ]
    case 'money_order':
      return [
        { key: 'reference', label: 'Serial number', kind: 'text', required: true, placeholder: 'From the money order' },
        AMOUNT('Amount'),
        RECEIVED,
        DETAIL.money_order,
      ]
    case 'cash':
      return [
        AMOUNT('Amount received'),
        RECEIVED,
        { key: 'reference', label: 'Receipt number', kind: 'text', required: false, placeholder: 'If you gave one' },
        DETAIL.cash,
      ]
    case 'card':
      return [{ key: 'bill_zip', label: 'Billing ZIP', kind: 'text', required: true }]
    case 'bank':
      return [
        { key: 'name_on_account', label: 'Name on the account', kind: 'text', required: true },
        {
          key: 'account_type', label: 'Account type', kind: 'select', required: true,
          options: [
            { value: 'checking', label: 'Checking' }, { value: 'savings', label: 'Savings' },
            { value: 'businessChecking', label: 'Business checking' },
          ],
        },
        { key: 'bill_zip', label: 'Billing ZIP', kind: 'text', required: true },
      ]
  }
}

/** What the member agreed to over the phone, kept on the payment. Never the account number. */
export function authorizationText(name: string, totalCents: number, accountType: string): string {
  const kind = accountType === 'savings' ? 'savings' : accountType === 'businessChecking' ? 'business checking' : 'checking'
  return `Read to the member and agreed: ${name.trim()} authorized a one-time debit of ${dollars(totalCents)} from their ${kind} account for these memberships.`
}

export const offerLabel = (termMonths: number): string =>
  termMonths % 12 === 0
    ? `${termMonths / 12} year${termMonths === 12 ? '' : 's'} · ${termMonths} months`
    : `${termMonths} month${termMonths === 1 ? '' : 's'}`

/** A comparable key for "is this still the person the operator answered about". */
export const personKey = (...parts: string[]): string => parts.map((p) => p.trim().toLowerCase().replace(/\s+/g, ' ')).join('|')

/**
 * Whether a new person's duplicate check lets the form be saved.
 * `searching`: a name worth checking is typed. `settled`: the lookup for exactly
 * what is typed has come back. A "different person" answer holds only for the key it was given for.
 */
export function dupGate(s: {
  searching: boolean; settled: boolean; candidates: number; dismissedKey: string | null; key: string
}): { asking: boolean; blocked: boolean } {
  const asking = s.searching && s.settled && s.candidates > 0 && s.dismissedKey !== s.key
  return { asking, blocked: s.searching && (!s.settled || asking) }
}

/** Calm wording for a phone payment whose outcome we do not know. */
export function unconfirmedText(instrument: 'card' | 'bank', transactionId: string | null, invoice: string | null): string {
  const what = instrument === 'card' ? 'The card may have been charged' : 'The bank account may have been debited'
  const ref = transactionId ? `this transaction id` : invoice ? 'this invoice number' : null
  return ref
    ? `${what}. Don't try again: note ${ref} and tell an administrator:`
    : `${what}. Don't try again: tell an administrator.`
}

/** A 202 from the phone route: held for review, not a recorded payment. */
export const isHeldForReview = (r: unknown): r is { code: 'held_for_review'; transaction_id: string; message: string } =>
  !!r && typeof r === 'object' && (r as { code?: unknown }).code === 'held_for_review'

/** One line describing a recent charge the operator might be about to repeat. */
export function recentChargeText(c: { when: string; amount_cents: number | null; titles: string[]; transaction_id: string | null; status?: string }): string {
  const at = new Date(c.when)
  const time = Number.isNaN(at.getTime()) ? c.when : at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  const amount = c.amount_cents != null ? dollars(c.amount_cents) : 'An amount we could not read'
  const what = c.titles.length ? ` for ${c.titles.join(', ')}` : ''
  const state = c.status === 'held' ? ' (held for review)' : c.status === 'unconfirmed' ? ' (not confirmed)' : ''
  const ref = c.transaction_id ? `, transaction ${c.transaction_id}` : ''
  return `${amount}${what} at ${time}${state}${ref}.`
}

export type CountryChoice = 'US' | 'CA' | 'OTHER'
/** The country a new person's record gets, from the form's select and its free-text box. */
export function countryValue(choice: CountryChoice, other: string): string | null {
  if (choice === 'US') return 'UNITED STATES'
  if (choice === 'CA') return 'CANADA'
  return other.trim() ? other.trim().toUpperCase() : null
}

export type PhoneFailure =
  | { kind: 'message'; text: string }
  | { kind: 'unconfirmed'; txn: string | null; invoice: string | null }

/**
 * How a failed card or eCheck payment reads. `status` is null when no answer
 * came back at all (a network error). A 5xx carrying our own JSON message (503
 * "Card payments aren't set up…", 502 "…Nothing was charged.") is shown as it
 * is and the form stays usable. Only `{code: 'unconfirmed'}`, a 5xx that is
 * not our JSON (a proxy's 502/504, a bare 500) and a network error get the calm
 * "may have been charged" wording. Null: not a 5xx, handle it as usual.
 */
export function phoneFailure(status: number | null, payload: unknown): PhoneFailure | null {
  if (status !== null && status < 500) return null
  const detail = payload && typeof payload === 'object' ? (payload as { detail?: unknown }).detail : undefined
  if (status !== null && typeof detail === 'string' && detail.trim()) return { kind: 'message', text: detail }
  const d = detail && typeof detail === 'object' ? detail as { transaction_id?: unknown; invoice_number?: unknown } : {}
  return {
    kind: 'unconfirmed',
    txn: typeof d.transaction_id === 'string' && d.transaction_id ? d.transaction_id : null,
    invoice: typeof d.invoice_number === 'string' && d.invoice_number ? d.invoice_number : null,
  }
}
